// Inheritance-mode-aware cascade priors (pure). Given the family's per-disease
// inheritance pattern (from the analyzer) and a relative's relationship to an
// index, return the carrier/at-risk prior + a band.
//
// ⚠️ The prior values are clinical decisions — review against standard cascade
// references. Conservative by design: anything that can't transmit → prior 0.
//
// v1 scope (see PLAN-cascade-priors-inheritance-aware.md):
//  - Dominant (AD / XLD / unknown-or-weak fallback): degree ladder 0.5 / 0.25 /
//    0.125 to 3rd degree.
//  - Autosomal recessive: first-degree of an AFFECTED index — parents & children
//    are obligate carriers (~1.0, band 'obligate'), unaffected full sibs 2/3; a
//    carrier-only index transmits at 0.5 to first-degree; beyond first degree → 0
//    (AR cascade is reproductive/first-degree focused).
//  - XLR / MT / YL: DEFERRED — fall back to the dominant degree default (this is
//    today's behaviour, so no regression). Precise sex-path / maternal-line rules
//    need sex-aware traversal + clinical review; tracked as a follow-up.

import { PATTERN, CONFIDENCE } from './constants';

export const CASCADE_DEPTH_CAP = 3;

const DOMINANT_LADDER = { 1: 0.5, 2: 0.25, 3: 0.125 };
const AR_SIB_CARRIER = 2 / 3; // unaffected full sib of an AR-affected individual

// Strong-only gate: use mode-specific priors only when the pattern is confidently
// known (and not ruled out); otherwise null → caller uses the dominant default.
export function resolveCascadeMode(pattern) {
  if (pattern && !pattern.ruledOut && pattern.confidence === CONFIDENCE.strong)
    return pattern.id;
  return null;
}

function dominantPrior(degree) {
  return { prior: DOMINANT_LADDER[degree] ?? 0, band: 'probabilistic' };
}

// rel: { kind: 'parent'|'child'|'fullSib'|..., degree, indexAffected }
function arPrior(rel) {
  if (rel.degree === 1) {
    if (rel.indexAffected) {
      if (rel.kind === 'parent' || rel.kind === 'child')
        return { prior: 1, band: 'obligate' };
      if (rel.kind === 'fullSib')
        return { prior: AR_SIB_CARRIER, band: 'probabilistic' };
    }
    // Carrier-only index transmits the single allele at 0.5 to first-degree.
    return { prior: 0.5, band: 'probabilistic' };
  }
  // AR cascade is first-degree focused; beyond that → not a v1 candidate.
  return { prior: 0, band: 'none' };
}

// mode: PATTERN.* | null (null = unknown/weak → dominant default).
export function cascadePrior(mode, rel) {
  if (mode === PATTERN.AR) return arPrior(rel);
  // PATTERN.XLR / MT / YL deferred → dominant degree default (no regression).
  return dominantPrior(rel.degree);
}
